import { useEffect, useState } from 'react';
import axios from 'axios';
import { GuideLink } from '@/components/RightPanel';
import { Rocket, ExternalLink, ArrowRight, AlertTriangle, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: 'searching', label: 'Searching' },
  { key: 'deal_announced', label: 'Deal Announced' },
  { key: 'vote_pending', label: 'Vote Pending' },
  { key: 'completed', label: 'Completed' },
];

// SPAC Lifecycle card — Insights tab. Self-fetches /spac-lifecycle;
// renders a stage stepper + SEC filing citations. Falls back to a
// static explainer when the Friday cron hasn't scanned this ticker yet.
export const SpacLifecycleCard = ({ ticker, onOpenTicker, onNavigate = null }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setLoaded(false);
    axios.get(`${API}/stocks/${ticker}/spac-lifecycle`, { withCredentials: true })
      .then((r) => { if (!cancelled) { setData(r.data); setLoaded(true); } })
      .catch(() => { if (!cancelled) { setData(null); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [ticker]);

  if (!loaded) return null;

  const stage = data?.stage;
  const isSuccessor = data?.role === 'successor';
  const isLiquidated = stage === 'liquidated';
  const isCompleted = stage === 'completed';
  const isTickerChanged = stage === 'ticker_changed';
  const stageIdx = STAGES.findIndex((s) => s.key === stage);

  // De-SPAC successor variant (e.g. TONT after GRAF→BIG3 merger):
  // show the origin story + the same SEC filings, since data
  // providers can lag on renaming the new entity for weeks.
  if (isSuccessor) {
    return (
      <div className="rounded-lg border border-slate-300/60 dark:border-slate-700/60 bg-slate-50/70 dark:bg-slate-900/40 p-3 space-y-2.5" data-testid="spac-successor-card">
        <div className="flex items-center justify-between flex-wrap gap-1">
          <div className="flex items-center gap-1.5">
            <Rocket className="w-4 h-4 text-slate-500 dark:text-slate-400" />
            <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">
              De-SPAC Origin
            </h4>
          </div>
          <GuideLink
            to="/learn/spac-lifecycle" onNavigate={onNavigate}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 dark:text-violet-400 hover:underline"
            data-testid="spac-learn-link"
          >
            <BookOpen className="w-3 h-3" /> What is a SPAC?
          </GuideLink>
        </div>
        <div className="px-2.5 py-2 rounded-md bg-violet-500/10 border border-violet-500/30 text-xs text-foreground/90" data-testid="spac-successor-banner">
          {ticker} came public through a SPAC merger: shell <button
            type="button"
            onClick={() => onOpenTicker?.(data.predecessor_ticker)}
            className="font-mono font-bold text-violet-700 dark:text-violet-300 hover:underline"
            data-testid="spac-predecessor-link"
          >{data.predecessor_ticker}</button>{data.predecessor_name ? ` (${data.predecessor_name})` : ''} merged
          with <b>{data.deal_target || 'its target company'}</b>. Company profile data from
          market providers can lag the rename — the SEC filings below describe the new business.
        </div>
        {Array.isArray(data?.evidence) && data.evidence.length > 0 && (
          <div data-testid="spac-successor-evidence">
            <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
              Merger filing trail {data.cik ? `· CIK ${data.cik}` : ''}
            </div>
            <div className="space-y-1">
              {data.evidence.slice(0, 5).map((ev, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px]">
                  <span className="font-mono font-bold text-slate-600 dark:text-slate-300 shrink-0">{ev.form}</span>
                  <span className="font-mono text-muted-foreground shrink-0">{ev.date}</span>
                  <span className="truncate text-muted-foreground">{ev.note}</span>
                  {ev.url && (
                    <a href={ev.url} target="_blank" rel="noopener noreferrer" className="shrink-0 text-violet-600 dark:text-violet-400 hover:underline inline-flex items-center" title="Open the source filing on SEC EDGAR" data-testid={`spac-successor-evidence-link-${i}`}>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-300/60 dark:border-slate-700/60 bg-slate-50/70 dark:bg-slate-900/40 p-3 space-y-2.5" data-testid="spac-lifecycle-card">
      <div className="flex items-center justify-between flex-wrap gap-1">
        <div className="flex items-center gap-1.5">
          <Rocket className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300">
            SPAC Lifecycle
          </h4>
        </div>
        <GuideLink
          to="/learn/spac-lifecycle" onNavigate={onNavigate}
          className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 dark:text-violet-400 hover:underline"
          data-testid="spac-learn-link"
        >
          <BookOpen className="w-3 h-3" /> What is a SPAC?
        </GuideLink>
      </div>

      {/* Stage stepper (or terminal liquidated banner) */}
      {isLiquidated ? (
        <div className="flex items-start gap-2 px-2.5 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-800 dark:text-red-300 text-xs" data-testid="spac-stage-liquidated">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            <b>Liquidated / wound down.</b> This SPAC never closed a deal — it was delisted and shareholders were redeemed from the trust account. The ticker is dead.
          </div>
        </div>
      ) : isTickerChanged ? (
        <div className="rounded-lg bg-sky-500/15 border-2 border-sky-500/50 px-4 py-3 text-sky-900 dark:text-sky-200" data-testid="spac-ticker-changed-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 shrink-0 text-sky-600 dark:text-sky-300" />
            <div className="flex-1 min-w-0 space-y-1">
              <div className="text-base sm:text-lg font-bold leading-tight">
                Ticker changed — {ticker} is retired{data?.new_ticker ? <>, now trades as <span className="font-mono">{data.new_ticker}</span></> : ''}
              </div>
              <div className="text-sm leading-relaxed">
                {/^.*U$/.test(ticker || '') ? 'This unit symbol was retired and ' : ''}This SPAC renamed its symbol ahead of its
                business combination{data?.deal_target ? <> with <b>{data.deal_target}</b></> : ''}. It is the same shell — the merger has <b>not</b> closed yet.
              </div>
            </div>
          </div>
          {data?.new_ticker && (
            <button
              type="button"
              onClick={() => onOpenTicker?.(data.new_ticker)}
              className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-md bg-sky-600 hover:bg-sky-500 text-white text-sm font-bold px-4 py-2 transition-colors"
              data-testid="spac-new-ticker-link"
            >
              Open {data.new_ticker} <span className="font-mono">→</span>
            </button>
          )}
        </div>
      ) : stage && stage !== 'unknown' ? (
        <>
          <div className="flex items-center flex-wrap gap-1" data-testid="spac-stage-stepper">
          {STAGES.map((s, i) => {
            const active = i === stageIdx;
            const done = stageIdx >= 0 && i < stageIdx;
            return (
              <span key={s.key} className="inline-flex items-center gap-1">
                <span
                  className={`px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wide border ${
                    active
                      ? 'bg-violet-600 text-white border-violet-500'
                      : done
                        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40'
                        : 'bg-muted text-muted-foreground border-border'
                  }`}
                  data-testid={`spac-stage-${s.key}${active ? '-active' : ''}`}
                >
                  {s.label}
                </span>
                {i < STAGES.length - 1 && <ArrowRight className="w-2.5 h-2.5 text-muted-foreground/50" />}
              </span>
            );
          })}
          </div>
          {(stage === 'deal_announced' || stage === 'vote_pending') && data?.deal_target && (
            <p className="text-[11px] text-foreground/85" data-testid="spac-deal-target">
              Announced merger target: <b>{data.deal_target}</b>
            </p>
          )}
        </>
      ) : (
        <p className="text-[11px] text-muted-foreground" data-testid="spac-stage-pending">
          Lifecycle not scanned yet — the weekly SEC EDGAR sweep will classify this SPAC&apos;s stage (Searching, Deal Announced, Vote Pending, Completed).
        </p>
      )}

      {/* De-SPAC completed banner with new-ticker link */}
      {isCompleted && (
        <div className="flex items-start gap-2 px-2.5 py-2 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-900 dark:text-amber-200 text-xs" data-testid="spac-completed-banner">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <div>
            <b>De-SPAC completed.</b> {data?.surviving_same_ticker ? (
              <>The merger closed and the operating company kept this ticker — {ticker} now represents the post-merger business, not a blank-check shell.</>
            ) : data?.new_ticker ? (
              <>
                This shell merged
                with {data?.deal_target ? <b>{data.deal_target}</b> : 'its target'} and now
                trades as <button
                  type="button"
                  onClick={() => onOpenTicker?.(data.new_ticker)}
                  className="font-mono font-bold text-violet-700 dark:text-violet-300 hover:underline"
                  data-testid="spac-new-ticker-link"
                >{data.new_ticker}</button>.
                The old ticker {ticker} is retired — any data under it is historical shell data.
              </>
            ) : (
              <>The merger closed and this ticker was retired or renamed. Check the filings below for the surviving company.</>
            )}
          </div>
        </div>
      )}

      {/* SEC filing evidence with citations */}
      {Array.isArray(data?.evidence) && data.evidence.length > 0 && (
        <div data-testid="spac-evidence-list">
          <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
            SEC filing trail {data.cik ? `· CIK ${data.cik}` : ''}
          </div>
          <div className="space-y-1">
            {data.evidence.slice(0, 5).map((ev, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`spac-evidence-row-${i}`}>
                <span className="font-mono font-bold text-slate-600 dark:text-slate-300 shrink-0">{ev.form}</span>
                <span className="font-mono text-muted-foreground shrink-0">{ev.date}</span>
                <span className="truncate text-muted-foreground">{ev.note}</span>
                {ev.url && (
                  <a
                    href={ev.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-violet-600 dark:text-violet-400 hover:underline inline-flex items-center"
                    title="Open the source filing on SEC EDGAR"
                    data-testid={`spac-evidence-link-${i}`}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pre-merger rename back-reference (TONT ← GRAF) */}
      {data?.renamed_from && (
        <p className="text-[10px] text-muted-foreground italic" data-testid="spac-renamed-from">
          Formerly traded as <span className="font-mono font-semibold">{data.renamed_from}</span> — same SPAC, renamed ahead of its pending merger.
        </p>
      )}

      {/* Former names — dead giveaway of the shell's history */}
      {Array.isArray(data?.former_names) && data.former_names.length > 0 && (
        <p className="text-[10px] text-muted-foreground italic" data-testid="spac-former-names">
          Former SEC names: {data.former_names.join(' · ')}
        </p>
      )}

      <p className="text-[10px] text-muted-foreground leading-relaxed">
        SPACs hold IPO cash in a trust (~$10/share) while hunting for a private company to merge with.
        Shareholders can redeem for trust value before the merger vote. Watch for dilution from sponsor
        promote shares and warrants after a deal closes.
        {data?.checked_at ? ` Last EDGAR check: ${String(data.checked_at).slice(0, 10)}.` : ''}
      </p>
    </div>
  );
};

export default SpacLifecycleCard;
